import React, { useEffect, useRef, useState } from 'react';
import { conditionsToFilter } from 'features/datasets/components/EvalConditions';
import EvalChips from 'shared/components/filters/evals/EvalChips';
import PropertyPickerPopover from 'shared/components/filters/PropertyPickerPopover';
import { listLineages } from 'features/evals3/api/evals3Api';
import { getSelectedTeamNumericId } from 'shared/utils/selectedTeamId';
import {
  AddControlButton,
  QUERY_SECTION_BODY_CLASS,
  QUERY_SECTION_HEADER_CLASS,
  QUERY_SECTION_LABEL_CLASS,
} from 'shared/components/filters/queryBuilderChrome';

/**
 * The EVALS section of the Traces filter (runs, spans, conversations): which
 * rows qualify by what the evals said about them, down to what one step of an
 * eval answered. The same builder as a dataset source's eval conditions; the
 * server compiles both with services/eval3/evalConditionsSql.
 *
 * `value` is the builder's lines (UI state, kept in the URL as `evals`);
 * evalFilterEntry() turns them into the one modular filter the list
 * endpoints read.
 */

let lineageCache = { teamId: null, at: 0, list: [] };

/** The team's evals (numeric team id, like every evals call), cached for a minute. */
export function useEvalLineages(teamIdArg) {
  const teamId = getSelectedTeamNumericId() || teamIdArg;
  const [lineages, setLineages] = useState(() => (lineageCache.teamId === teamId ? lineageCache.list : []));
  useEffect(() => {
    if (!teamId) return undefined;
    if (lineageCache.teamId === teamId && Date.now() - lineageCache.at < 60e3) { setLineages(lineageCache.list); return undefined; }
    let cancelled = false;
    Promise.resolve(listLineages(teamId))
      .then((l) => { lineageCache = { teamId, at: Date.now(), list: l || [] }; if (!cancelled) setLineages(l || []); })
      .catch(() => { if (!cancelled) setLineages([]); });
    return () => { cancelled = true; };
  }, [teamId]);
  return lineages;
}

/** The lines → the modular filter entry, or null when no line is complete. */
export function evalFilterEntry(lines) {
  const conditions = conditionsToFilter(Array.isArray(lines) ? lines : []);
  if (!conditions.length) return null;
  return { id: 'evals', category: 'evals', field: 'eval_conditions', operator: 'is', value: conditions };
}

/** Append the eval entry to a list of modular filters (a new array). */
export function withEvalFilter(modularFilters, lines) {
  const entry = evalFilterEntry(lines);
  return entry ? [...(modularFilters || []), entry] : (modularFilters || []);
}

/** How many complete eval conditions the lines hold (for the Filter badge). */
export function evalConditionCount(lines) {
  return conditionsToFilter(Array.isArray(lines) ? lines : []).length;
}

/** The lines kept in the URL as `evals`. */
export function readEvalLines(searchParams) {
  try {
    const raw = searchParams.get('evals');
    if (!raw) return [];
    const parsed = JSON.parse(decodeURIComponent(raw));
    return Array.isArray(parsed) ? parsed.filter((c) => c && typeof c === 'object' && c.key) : [];
  } catch {
    return [];
  }
}
export function writeEvalLines(next, lines) {
  const list = Array.isArray(lines) ? lines : [];
  if (list.length) next.set('evals', encodeURIComponent(JSON.stringify(list)));
  else next.delete('evals');
}

export default function EvalFilterSection({ teamId, value, onChange, label = 'EVALS' }) {
  const addRef = useRef(null);
  const [picking, setPicking] = useState(false);
  const lines = Array.isArray(value) ? value : [];
  return (
    <div className="bg-transparent" data-testid="eval-filter-section">
      <div className={QUERY_SECTION_HEADER_CLASS}>
        <h3 className={QUERY_SECTION_LABEL_CLASS}>{label}</h3>
      </div>
      <div className={QUERY_SECTION_BODY_CLASS}>
        <EvalChips teamId={teamId} lines={lines} onChange={onChange} />
        <div>
          <AddControlButton buttonRef={addRef} onClick={() => setPicking(true)} />
        </div>
      </div>
      <PropertyPickerPopover open={picking} onClose={() => setPicking(false)} anchorRef={addRef} teamId={teamId}
        scope="global" only="evals" onPick={() => {}} onPickEval={(line) => onChange([...lines, line])} />
    </div>
  );
}
